import { StreamUrl } from '@/classes/streamurl'
import { KeyDict } from '@/types'
import { StreamType } from '@/types/streams'
import { Ref } from '@vue/composition-api'
import useVuelidate, { ValidatorFn } from '@vuelidate/core'
import { required, helpers } from '@vuelidate/validators'

const noSlashInKey = (key: string): boolean => !key.includes('/')
const isRtmpUrl = (url: string): boolean => {
  if (!url.startsWith('rtmp://') && !url.startsWith('rtmps://') && !url.startsWith('rtsp://')) return false
  const args = StreamUrl.removeTrailingSlash(url).split('/')
  const isAzure = url.includes('media.azure.net')
  if (!isAzure && args.length !== 4) return false
  if (isAzure && args.length !== 5) return false
  return true
}
const isCorrectGop = (gop: number): boolean => (gop >= 0 && gop < 100)

const inPortRange = (port: number): boolean => (port > 0 && port < 65535 )

const checkPid = (pid: number|undefined|null, isOutput: boolean): boolean => {
  if ((pid !== undefined && pid !== null) && ((isOutput ? pid < 64 : pid < 1) || pid > 8000)) return false
  return true
}

const checkPidValidator = (isOutput: boolean): ValidatorFn<number|undefined|null> =>
(pid: number|undefined|null): boolean => {
  return checkPid(pid, isOutput)
}

const inPidRange = (isOutput: boolean): ValidatorFn<KeyDict<number|'auto'|undefined>> =>
(tracks: KeyDict<number|'auto'|undefined> ): boolean => {
  let inRange = true
  Object.values(tracks).forEach((value) => {
    if (value && value !== 'auto' && !checkPid(value, isOutput)) inRange = false
  })
  return inRange
}


export const checkTracks = (streamType: StreamType): ValidatorFn<KeyDict<number|'auto'|undefined>> =>
 (tracks: KeyDict<number|'auto'|undefined>): boolean => {
  if (streamType === StreamType.Output) {
    return tracks.Mixer ? true : false
  }
  return true
}

export const checkVideoPid = (streamType: StreamType): ValidatorFn<number|undefined> =>
 (videoPid: number|undefined): boolean => {
  if (streamType === StreamType.Output) {
    return videoPid ? true : false
  }
  return true
}

interface RtmpInputStreamValidatorParams {
  name: Ref<string|null>
  url: Ref<string|null>
  key: Ref<string|null>
}

interface SrtConnectionInputStreamValidatorParams {
  name: Ref<string|null>
  host: Ref<string|null>
  port: Ref<number|null>
  outputPort: Ref<number|null>
}

const useSrtTrackValidator = (tracks: Ref<KeyDict<number|'auto'|undefined>>,
  smptePid: Ref<number|undefined>, sctePid: Ref<number|undefined>,
  videoPid: Ref<number|undefined>, streamType: StreamType) => {
  const isOutput = streamType === StreamType.Output
  const pidErrorMessage = isOutput ? 'PIDs should be between 64 & 8000' : 'PIDs should be between 0 & 8000'
  const rules = {
    tracks: {
      isPidInRange: helpers.withMessage(pidErrorMessage, inPidRange(isOutput)),
      checkTracks: helpers.withMessage('Fill in at least one audio PID',
        checkTracks(streamType))
    },
    smptePid: {
      isPidInRange: helpers.withMessage(pidErrorMessage, checkPidValidator(false))
    },
    sctePid: {
      isPidInRange: helpers.withMessage(pidErrorMessage, checkPidValidator(false))
    },
    videoPid: {
      isPidInRange: helpers.withMessage(pidErrorMessage, checkPidValidator(isOutput)),
      checkVideoPid: helpers.withMessage('Fill in a video PID',
        checkVideoPid(streamType))
    }
  }
  return {
    validator: useVuelidate(rules, { tracks, smptePid, sctePid, videoPid })
  }
}

const useSrtConnectionInputStreamValidator = (params: SrtConnectionInputStreamValidatorParams,
   showHostPort: boolean, showOutputPort: boolean) => {
  const { name, host, port, outputPort } = params
  if (showOutputPort) {
    const rules = {
      name: {
        required: helpers.withMessage('Required', required)
      },
      host: {
        required: helpers.withMessage('Required', required)
      },
      port: {
        required: helpers.withMessage('Required', required),
        inPortRange: helpers.withMessage('Port is between 0 & 65535', inPortRange)
      },
      outputPort: {
        required: helpers.withMessage('Required', required),
        inPortRange: helpers.withMessage('Port is between 0 & 65535', inPortRange)
      }
    }
    return {
      validator: useVuelidate(rules, { name, host, port, outputPort })
    }
  }
  if (showHostPort) {
    const rules = {
      name: {
        required: helpers.withMessage('Required', required)
      },
      host: {
        required: helpers.withMessage('Required', required)
      },
      port: {
        required: helpers.withMessage('Required', required),
        inPortRange: helpers.withMessage('Port is between 0 & 65535', inPortRange)
      },
      outputPort: {
      }
    }
    return {
      validator: useVuelidate(rules, { name, host, port, outputPort })
    }
  }
  const rules = {
    name: {
      required: helpers.withMessage('Required', required)
    },
    host: {
    },
    port: {
    },
    outputPort: {
    }
  }
  return {
    validator: useVuelidate(rules, { name, host, port, outputPort })
  }
}


const useReencodeSettingsValidator = (gopSize: Ref<number>) => {
  const rules = {
    gopSize: {
      required: helpers.withMessage('Required', required),
      isCorrectGop: helpers.withMessage('GOP size should be a number between 0 and 100', isCorrectGop)
    }
  }
  return {
    validator: useVuelidate(rules, { gopSize })
  }
}

const useRtmpInputStreamValidator = (params: RtmpInputStreamValidatorParams) => {
  const { name, url, key } = params
  const rules = {
    name: {
      required: helpers.withMessage('Required', required)
    },
    url: {
      required: helpers.withMessage('Required', required),
      isRtmpUrl: helpers.withMessage('Wrong stream URL. (Format: rtmp://hostname/app)', isRtmpUrl)
    },
    key: {
      required: helpers.withMessage('Required', required),
      noSlashInKey: helpers.withMessage('Wrong stream key (only letters and numbers allowed)', noSlashInKey)
    }
  }
  return {
    validator: useVuelidate(rules, { name, url, key })
  }
}

export {
  useRtmpInputStreamValidator,
  useSrtConnectionInputStreamValidator,
  useReencodeSettingsValidator,
  useSrtTrackValidator
}
